import React, { useState, useMemo } from 'react';
import { Character } from '../../types';

interface CharacterListProps {
  characters: Character[];
  selectedCharacter: Character | null;
  onSelectCharacter: (character: Character) => void;
}

interface Filters {
  search: string;
  type: string;
  minSpeed: string;
  maxSpeed: string;
  minAttack: string;
  maxAttack: string;
  minDefense: string;
  maxDefense: string;
  minHealth: string;
  maxHealth: string;
  sortBy: 'name' | 'type' | 'speed' | 'attack' | 'defense' | 'health';
  sortOrder: 'asc' | 'desc';
}

const CharacterList: React.FC<CharacterListProps> = ({
  characters,
  selectedCharacter,
  onSelectCharacter,
}) => {
  const [filters, setFilters] = useState<Filters>({
    search: '',
    type: '',
    minSpeed: '',
    maxSpeed: '',
    minAttack: '',
    maxAttack: '',
    minDefense: '',
    maxDefense: '',
    minHealth: '',
    maxHealth: '',
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);

  // Get unique character types for filter dropdown
  const characterTypes = useMemo(() => {
    const types = new Set<string>();
    characters.forEach(c => {
      if (c.types && c.types.length > 0) {
        c.types.forEach(type => types.add(type));
      } else {
        // Fallback to old type string
        types.add(c.type);
      }
    });
    return Array.from(types).sort();
  }, [characters]);

  // Filter and sort characters
  const filteredCharacters = useMemo(() => {
    let filtered = characters.filter(character => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesName = character.name.toLowerCase().includes(searchLower);
        const matchesType = character.type.toLowerCase().includes(searchLower);
        const matchesKeywords = character.keywords.some(k => 
          k.name.toLowerCase().includes(searchLower)
        );
        if (!matchesName && !matchesType && !matchesKeywords) return false;
      }

      // Type filter - check if the selected type is in the character's types array
      if (filters.type) {
        if (character.types && character.types.length > 0) {
          if (!character.types.includes(filters.type)) return false;
        } else {
          // Fallback to old type string
          if (character.type !== filters.type) return false;
        }
      }

      // Speed filters
      if (filters.minSpeed && character.baseStats.speed < parseInt(filters.minSpeed)) return false;
      if (filters.maxSpeed && character.baseStats.speed > parseInt(filters.maxSpeed)) return false;

      // Attack filters
      if (filters.minAttack && character.baseStats.attackBonus < parseInt(filters.minAttack)) return false;
      if (filters.maxAttack && character.baseStats.attackBonus > parseInt(filters.maxAttack)) return false;

      // Defense filters
      if (filters.minDefense && character.baseStats.defenseBonus < parseInt(filters.minDefense)) return false;
      if (filters.maxDefense && character.baseStats.defenseBonus > parseInt(filters.maxDefense)) return false;

      // Health filters
      if (filters.minHealth && character.baseStats.health < parseInt(filters.minHealth)) return false;
      if (filters.maxHealth && character.baseStats.health > parseInt(filters.maxHealth)) return false;

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      let aVal: any, bVal: any;

      switch (filters.sortBy) {
        case 'name':
          // Remove "The " prefix for sorting purposes
          aVal = a.name.toLowerCase().replace(/^the\s+/, '');
          bVal = b.name.toLowerCase().replace(/^the\s+/, '');
          break;
        case 'type':
          aVal = a.type.toLowerCase();
          bVal = b.type.toLowerCase();
          break;
        case 'speed':
          aVal = a.baseStats.speed;
          bVal = b.baseStats.speed;
          break;
        case 'attack':
          aVal = a.baseStats.attackBonus;
          bVal = b.baseStats.attackBonus;
          break;
        case 'defense':
          aVal = a.baseStats.defenseBonus;
          bVal = b.baseStats.defenseBonus;
          break;
        case 'health':
          aVal = a.baseStats.health;
          bVal = b.baseStats.health;
          break;
        default:
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
      }

      if (aVal < bVal) return filters.sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return filters.sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [characters, filters]);

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      type: '',
      minSpeed: '',
      maxSpeed: '',
      minAttack: '',
      maxAttack: '',
      minDefense: '',
      maxDefense: '',
      minHealth: '',
      maxHealth: '',
      sortBy: 'name',
      sortOrder: 'asc',
    });
  };

  return (
    <div className="character-list-panel">
      <div className="filter-section">
        {/* Search Bar - Always visible */}
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search by name, type, or keyword..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
        </div>

        {/* Collapsible Filters */}
        <div className={`collapsible-filters ${filtersCollapsed ? 'collapsed' : 'expanded'}`}>
          {/* Quick Filters */}
          <div className="filter-row">
            <div className="filter-group">
              <label>Type</label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="filter-select"
              >
                <option value="">All Types</option>
                {characterTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Sort By</label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value as any)}
                className="filter-select"
              >
                <option value="name">Name</option>
                <option value="type">Type</option>
                <option value="speed">Speed</option>
                <option value="attack">Attack</option>
                <option value="defense">Defense</option>
                <option value="health">Health</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Order</label>
              <select
                value={filters.sortOrder}
                onChange={(e) => handleFilterChange('sortOrder', e.target.value as any)}
                className="filter-select"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>

          {/* Advanced Filters Toggle */}
          <button
            className="toggle-advanced-btn"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            {showAdvancedFilters ? '▼' : '▶'} Advanced Filters
          </button>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="advanced-filters">
              <div className="filter-row">
                <div className="filter-group-range">
                  <label>Speed</label>
                  <div className="range-inputs">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.minSpeed}
                      onChange={(e) => handleFilterChange('minSpeed', e.target.value)}
                      className="range-input"
                    />
                    <span>-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.maxSpeed}
                      onChange={(e) => handleFilterChange('maxSpeed', e.target.value)}
                      className="range-input"
                    />
                  </div>
                </div>

                <div className="filter-group-range">
                  <label>Attack Bonus</label>
                  <div className="range-inputs">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.minAttack}
                      onChange={(e) => handleFilterChange('minAttack', e.target.value)}
                      className="range-input"
                    />
                    <span>-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.maxAttack}
                      onChange={(e) => handleFilterChange('maxAttack', e.target.value)}
                      className="range-input"
                    />
                  </div>
                </div>
              </div>

              <div className="filter-row">
                <div className="filter-group-range">
                  <label>Defense Bonus</label>
                  <div className="range-inputs">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.minDefense}
                      onChange={(e) => handleFilterChange('minDefense', e.target.value)}
                      className="range-input"
                    />
                    <span>-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.maxDefense}
                      onChange={(e) => handleFilterChange('maxDefense', e.target.value)}
                      className="range-input"
                    />
                  </div>
                </div>

                <div className="filter-group-range">
                  <label>Health</label>
                  <div className="range-inputs">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.minHealth}
                      onChange={(e) => handleFilterChange('minHealth', e.target.value)}
                      className="range-input"
                    />
                    <span>-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.maxHealth}
                      onChange={(e) => handleFilterChange('maxHealth', e.target.value)}
                      className="range-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Clear Filters Button */}
          <button className="clear-filters-btn" onClick={clearFilters}>
            Clear All Filters
          </button>
        </div>

        {/* Results Count */}
        <div className="results-count">
          Showing {filteredCharacters.length} of {characters.length} characters
        </div>

        {/* Collapse Toggle Button */}
        <button
          className="collapse-filters-btn"
          onClick={() => setFiltersCollapsed(!filtersCollapsed)}
          aria-label={filtersCollapsed ? 'Expand filters' : 'Collapse filters'}
          data-collapsed={filtersCollapsed ? 'true' : undefined}
        >
          <span className="collapse-filters-btn-text">
            {filtersCollapsed ? 'Show Filters' : 'Hide Filters'}
          </span>
        </button>
      </div>

      {/* Character List */}
      <div className="character-list-scroll">
        {filteredCharacters.map(character => (
          <div
            key={character.characterId}
            className={`character-list-item ${
              selectedCharacter?.characterId === character.characterId ? 'selected' : ''
            }`}
            onClick={() => onSelectCharacter(character)}
          >
            <div className="character-list-item-header">
              <h3>{character.name}</h3>
              {character.types && character.types.length > 0 ? (
                <div className="character-list-types">
                  {character.types.map((type, index) => (
                    <span key={index} className="character-list-type-badge">{type}</span>
                  ))}
                </div>
              ) : (
                <span className="character-list-type">{character.type}</span>
              )}
            </div>
            <div className="character-list-stats">
              <span>❤️ {character.baseStats.health}</span>
              <span>⚔️ +{character.baseStats.attackBonus}</span>
              <span>🛡️ +{character.baseStats.defenseBonus}</span>
              <span>⚡ {character.baseStats.speed}</span>
            </div>
          </div>
        ))}

        {filteredCharacters.length === 0 && (
          <div className="no-results">
            <p>No characters match your filters</p>
            <button onClick={clearFilters} className="btn-secondary btn-sm">
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CharacterList;
